import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { approvalsApi, type ApprovalRequest } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import ApprovalPanel from '../components/approvals/ApprovalPanel';

/**
 * The approvals inbox (EP-26 FE-7 ST-5). "For me to decide" is exactly what the
 * server says this person may decide NOW — they hold the permission at the
 * item's scope, did not make the request, are not excluded from it and have not
 * already voted. Nothing here decides access; it lists and forwards.
 * "Mine" is the caller's own open requests: to follow or withdraw.
 */
function left(ts?: number): string {
  if (!ts) return '';
  const s = ts - Date.now() / 1000;
  if (s <= 0) return 'expired';
  const h = Math.floor(s / 3600);
  return h >= 48 ? `${Math.floor(h / 24)}d left` : h >= 1 ? `${h}h left` : `${Math.max(1, Math.round(s / 60))}m left`;
}

/** Why an agent action was held, in words. */
const REFUSAL: Record<string, string> = {
  no_authority: "the agent's person lacks the permission here",
  agent_ceiling: 'an agent may not do this itself',
  governor_misaligned: 'the governor judged it out of line',
};

/** The work item to open for a request: the item itself, or for an authority request
 *  the item its action concerns -- never the authority record. */
function itemToOpen(r: ApprovalRequest): string | undefined {
  return r.itemType === 'authority' ? (r.action?.workItemId || undefined) : r.itemId;
}

/** Each request's kind of authority (hs-pmo/ep43#10), as a badge. */
const CATEGORY: Record<string, { label: string; cls: string }> = {
  'agent-authority': { label: 'Agent authority', cls: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' },
  'design-waiver': { label: 'Design waiver', cls: 'bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300' },
  // A tool made the standard for a category (hs-pmo/ep43#46): super admins only.
  'technology-standard': { label: 'Technology standard', cls: 'bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300' },
  'token-spend': { label: 'Token spend', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' },
  evidence: { label: 'Research evidence', cls: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300' },
  operations: { label: 'Operations', cls: 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300' },
  training: { label: 'Training', cls: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' },
  workflow: { label: 'Workflow', cls: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' },
};

function CategoryBadge({ category }: { category?: string }) {
  if (!category) return null;
  const c = CATEGORY[category] || { label: category, cls: CATEGORY.workflow.cls };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded mr-1.5 ${c.cls}`}>{c.label}</span>;
}

function tokens(n?: number | null): string {
  return n == null ? '?' : n.toLocaleString();
}

/** What a request asks for, in words: "Asks to move PO46-12 Fix X to done". */
function asks(r: ApprovalRequest): string {
  const what = [r.item?.key || r.itemKey, r.item?.title || r.itemTitle].filter(Boolean).join(' ') || r.itemId || 'the item';
  if (r.itemType === 'authority') return `Asks to ${r.action?.kind || 'take an action'} on ${what}`;
  if (r.designWaiver) return `Asks to ship ${what} without fixing it (${r.designWaiver.severity}${r.designWaiver.principleId ? `, ${r.designWaiver.principleId}` : ''})`;
  if (r.tokenSpend) return r.tokenSpend.kind === 'raise-budget'
    ? `Asks to raise the daily runner token budget to ${tokens(r.tokenSpend.amount)} (for ${what})`
    : `Asks to let runner ${r.tokenSpend.runnerSessionId || ''} continue over today's budget on ${what}`;
  if (r.evidence) return `Asks to release evidence "${r.evidence.fileName || r.evidence.evidenceId}" for ${what}`;
  return `Asks to move ${what} to ${r.to}`;
}

/**
 * One of MY requests (hs-pmo/ep19#12, Zane: "what is the ask here, this needs to be clearer"): what
 * it asks, who it is waiting for -- never me, since nobody approves their own request -- which
 * session asked, and what I can do: withdraw it, or open the item.
 */
function MineRow({ r, selected, onSelect, onOpenItem, onWithdrawn }: {
  r: ApprovalRequest; selected: boolean; onSelect: () => void; onOpenItem: () => void; onWithdrawn: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const w = r.waitingFor;
  const others = w && w.count > 0
    ? `${w.people.join(', ')}${w.count > w.people.length ? ` and ${w.count - w.people.length} more` : ''}`
    : '';
  const withdraw = async () => {
    if (!window.confirm('Withdraw this request? Nothing will move.')) return;
    setBusy(true);
    try { await approvalsApi.withdraw(r.requestId); onWithdrawn(); } catch { /* the panel shows why */ } finally { setBusy(false); }
  };
  return (
    <li>
      <button type="button" onClick={onSelect}
        className={`w-full text-left px-3 py-2 rounded-md border ${selected ? 'border-accent bg-accent/5' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-900 dark:text-gray-100"><CategoryBadge category={r.category} />{asks(r)}</span>
          <span className="text-xs tabular-nums text-amber-700 dark:text-amber-300 shrink-0">{r.approvals}/{r.required}</span>
        </div>
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">
          {w?.youCanDecide
            ? <>You can approve this (it was filed for you).{others ? <> Also: {others}.</> : null}</>
            : others
              ? <>Waiting for: {others} — holders of <code>{w?.permission}</code>{w?.project ? ` on ${w.project}` : ''}. You can&rsquo;t approve your own request.</>
              : <span className="text-amber-700 dark:text-amber-400">Nobody else can approve this: no one else holds <code>{w?.permission || r.permission}</code>{w?.project ? ` on ${w.project}` : ''}. Withdraw it, or ask an admin to grant the permission.</span>}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 flex flex-wrap gap-x-2">
          <span>asked by {r.requester?.label || r.requester?.session || r.requestedBy}</span>
          {r.expiresAt ? <span>{left(r.expiresAt)}</span> : null}
        </div>
      </button>
      <div className="mt-1 ml-3 flex gap-3 text-xs">
        <button type="button" disabled={busy} onClick={withdraw} className="text-red-600 hover:underline disabled:opacity-50">Withdraw</button>
        {itemToOpen(r) ? <button type="button" onClick={onOpenItem} className="text-accent hover:underline">Open the item</button> : null}
      </div>
    </li>
  );
}

function Row({ r, selected, onSelect, onOpenItem }: {
  r: ApprovalRequest; selected: boolean; onSelect: () => void; onOpenItem: () => void;
}) {
  const authority = r.itemType === 'authority';
  return (
    <li>
      <button type="button" onClick={onSelect}
        className={`w-full text-left px-3 py-2 rounded-md border ${selected ? 'border-accent bg-accent/5' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
            <CategoryBadge category={r.category} />
            {r.agentFinish ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 mr-1.5">Agent finished</span> : null}
            {(r.item?.key || r.itemKey) ? <span className="font-mono text-xs text-gray-500 mr-1.5">{r.item?.key || r.itemKey}</span> : null}
            {r.item?.title || r.itemTitle || r.itemId}
          </span>
          <span className="text-xs tabular-nums text-amber-700 dark:text-amber-300 shrink-0">{r.approvals}/{r.required}</span>
        </div>
        <div className="mt-0.5 text-xs text-gray-500 flex flex-wrap gap-x-2">
          {authority
            ? <span>{r.action?.kind}{r.action?.refusal ? ` — held: ${REFUSAL[r.action.refusal] || r.action.refusal}` : ''}</span>
            : r.tokenSpend
              ? <span>{r.tokenSpend.kind === 'raise-budget' ? `raise budget to ${tokens(r.tokenSpend.amount)}` : 'allow this run over budget'} · used {tokens(r.tokenSpend.usedToday)} of {tokens(r.tokenSpend.budget)}</span>
            : r.evidence
              ? <span>evidence {r.evidence.fileName || r.evidence.evidenceId} · {r.evidence.findings?.length || 0} example finding(s)</span>
              : <span>→ {r.to}</span>}
          {r.item?.chain?.[0] ? <span>{r.item.chain[0].key} {r.item.chain[0].title}</span> : null}
          <span>needs {r.permission}</span>
          <span>from {r.requester?.session || r.requestedBy}</span>
          {r.origin ? <span>origin {r.origin}</span> : null}
          {r.expiresAt ? <span>{left(r.expiresAt)}</span> : null}
        </div>
      </button>
      {selected && itemToOpen(r) ? (
        <button type="button" onClick={onOpenItem} className="mt-1 ml-3 text-xs text-accent hover:underline">Open the item</button>
      ) : null}
    </li>
  );
}

export default function ApprovalsPage() {
  const setDetailItemId = useAppStore((s) => s.setDetailItemId);
  const [pending, setPending] = useState<ApprovalRequest[] | null>(null);
  const [mine, setMine] = useState<ApprovalRequest[]>([]);
  // ?request=<id> opens that request: the link Research (and notices) send a decider to.
  const [params] = useSearchParams();
  const [selected, setSelected] = useState<string>(params.get('request') || '');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    Promise.all([approvalsApi.pending(), approvalsApi.mine()])
      .then(([p, m]) => { setPending(p); setMine(m); setError(''); })
      .catch((e) => { setError(String(e?.message || e)); setPending([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Inbox className="w-5 h-5" /> Approvals
        </h1>
        <button type="button" onClick={load} className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-accent">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr),minmax(0,1.2fr)]">
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 mb-2">
              <ShieldCheck className="w-4 h-4" /> For me to decide
            </h2>
            {pending === null ? (
              <p className="text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
            ) : pending.length === 0 ? (
              <p className="text-sm text-gray-500">Nothing is waiting on you.</p>
            ) : (
              <ul className="space-y-2">
                {pending.map((r) => (
                  <Row key={r.requestId} r={r} selected={r.requestId === selected}
                    onSelect={() => setSelected(r.requestId)} onOpenItem={() => { const id = itemToOpen(r); if (id) setDetailItemId(id); }} />
                ))}
              </ul>
            )}
          </section>
          <section>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">My requests</h2>
            {mine.length === 0 ? (
              <p className="text-sm text-gray-500">You have no open requests.</p>
            ) : (
              <ul className="space-y-2">
                {mine.map((r) => (
                  <MineRow key={r.requestId} r={r} selected={r.requestId === selected}
                    onWithdrawn={() => load()}
                    onSelect={() => setSelected(r.requestId)} onOpenItem={() => { const id = itemToOpen(r); if (id) setDetailItemId(id); }} />
                ))}
              </ul>
            )}
          </section>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div>
          {/* By id, not only from the lists: a link may name a request this person follows but is
              not listed for, or one already decided -- the panel says why it can't be decided. */}
          {selected ? (
            <ApprovalPanel key={selected} requestId={selected} onChanged={() => load()} />
          ) : (
            <p className="text-sm text-gray-500">Choose a request to see what is asked, the votes so far and when it expires.</p>
          )}
        </div>
      </div>
    </div>
  );
}
